import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Navbar from '../components/Navbar'
import HeroSection from '../components/HeroSection'
import StatCard from '../components/ui/StatCard'
import Section from '../components/ui/Section'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import Leaderboard from '../components/Leaderboard'
import Accordion from '../components/ui/Accordion'
import SocialMediaCard from '../components/SocialMediaCard'
import Sponsors from '../components/Sponsors'
import Footer from '../components/Footer'
import { TESTIMONIALS, APP_CONFIG, FAQ_DATA } from '../data/constants.jsx'
import { getIcon } from '../hooks/useIcons.jsx'
import { useAuth } from '../contexts/AuthContext'
import apiService from '../services/api'

const Homepage = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  // Remove modal states - no longer needed
  const [monthlyLeaderboard, setMonthlyLeaderboard] = useState([])
  const [monthlyMeta, setMonthlyMeta] = useState(null)
  const [dailyLeaderboard, setDailyLeaderboard] = useState([])
  const [leaderboardLoading, setLeaderboardLoading] = useState(true)
  const [previousMonthTop, setPreviousMonthTop] = useState(null)
  const { user, login, register, demoLogin, logout, loading, error } = useAuth()

  // Load leaderboard data on component mount
  useEffect(() => {
    const loadLeaderboardData = async () => {
      try {
        setLeaderboardLoading(true)
        const [monthlyData, dailyData, previousMonthData] = await Promise.all([
          apiService.getMonthlyLeaderboard(),
          apiService.getDailyLeaderboard(),
          apiService.getPreviousMonthTopPerformers()
        ])
        
        // Transform data for Leaderboard component with standardized field names
        setMonthlyLeaderboard((monthlyData.leaderboard || monthlyData).map(item => ({
          rank: item.rank || 0,
          name: item.name || 'Unknown',
          points: item.total_points || 0,
          wakeup_count: item.wakeup_count || 0,
          total_helped: item.total_helped || 0,
          total_calls: item.total_calls || 0,
          avatar: item.name?.charAt(0) || 'U',
          location: item.country || 'Unknown'
        })))

        setMonthlyMeta({
          month_name: monthlyData.month_name,
          year: monthlyData.year
        })
        
        setDailyLeaderboard((dailyData.leaderboard || []).map(item => ({
          rank: item.rank || 0,
          name: item.name || 'Unknown',
          wokenUp: item.total_helped || 0,
          points: item.total_points || 0,
          wakeup_count: item.wakeup_count || 0,
          total_helped: item.total_helped || 0,
          total_calls: item.total_calls || 0,
          avatar: item.name?.charAt(0) || 'U',
          location: item.country || 'Unknown'
        })))

        setPreviousMonthTop(previousMonthData)
      } catch (err) {
        console.error('Failed to load leaderboard data:', err)
        // Fall back to empty arrays if API fails
        setMonthlyLeaderboard([])
        setDailyLeaderboard([])
        setPreviousMonthTop(null)
      } finally {
        setLeaderboardLoading(false)
      }
    }

    loadLeaderboardData()
  }, [])

  return (
    <div className="min-h-screen bg-background">
      <Navbar user={user} onLogout={logout} />
      <HeroSection />

      {/* Previous Month Top Performers - Compact */}
      {previousMonthTop && previousMonthTop.top_performers && previousMonthTop.top_performers.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 py-6">
          <h3 className="text-xl font-bold text-center text-gray-800 mb-4">
            🏆 {previousMonthTop.month_name} {previousMonthTop.year} Top Performers
          </h3>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {previousMonthTop.top_performers.map((performer, index) => (
              <div key={index} className={`flex flex-col sm:flex-row sm:items-center justify-between p-3 gap-2 ${index !== previousMonthTop.top_performers.length - 1 ? 'border-b border-gray-100' : ''}`}>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}</span>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
                    <span className="font-semibold text-gray-800">{performer.name}</span>
                    <span className="text-xs sm:text-sm text-gray-500">{performer.country}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm ml-11 sm:ml-0 flex-wrap">
                  <span className="text-gray-600">
                    <span className="font-medium">Rise:</span> {performer.wakeup_count}
                  </span>
                  <span className="text-gray-600">
                    <span className="font-medium">Call:</span> {performer.total_calls}
                  </span>
                  <span className="text-gray-600">
                    <span className="font-medium">Help:</span> {performer.total_helped}
                  </span>
                  <span className="text-gray-600">
                    <span className="font-medium">Point:</span> {performer.total_points}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Section title={t('homepage.leaderboard.title')} subtitle={t('homepage.leaderboard.subtitle')}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {leaderboardLoading ? (
            <>
              <Card className="p-8">
                <div className="animate-pulse">
                  <div className="h-6 bg-gray-200 rounded mb-4"></div>
                  <div className="space-y-3">
                    {[1,2,3,4,5].map(i => (
                      <div key={i} className="h-4 bg-gray-200 rounded"></div>
                    ))}
                  </div>
                </div>
              </Card>
              <Card className="p-8">
                <div className="animate-pulse">
                  <div className="h-6 bg-gray-200 rounded mb-4"></div>
                  <div className="space-y-3">
                    {[1,2,3,4,5].map(i => (
                      <div key={i} className="h-4 bg-gray-200 rounded"></div>
                    ))}
                  </div>
                </div>
              </Card>
            </>
          ) : (
            <>
              <Leaderboard
                title={monthlyMeta ? `${t('homepage.leaderboard.monthly')} - ${monthlyMeta.month_name}` : t('homepage.leaderboard.monthly')}
                data={monthlyLeaderboard.slice(0, 10)}
                type="points"
              />
              <Leaderboard
                title={t('homepage.leaderboard.daily')}
                data={dailyLeaderboard.slice(0, 10)}
                type="points"
              />
            </>
          )}
        </div>
      </Section>

      {/* Why This Project is Important Section */}
      <Section
        title={t('homepage.importance.title')}
        subtitle={t('homepage.importance.subtitle')}
        className="bg-white"
      >
        <div className="max-w-6xl mx-auto">
          {/* Main Problem & Solution */}
          <Card className="p-8 mb-12 bg-gradient-to-br from-green-50 to-blue-50 border-none">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white">{getIcon('sun', 32)}</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">{t('homepage.importance.mainHeading')}</h3>
            </div>
            
            <div className="space-y-4 text-gray-700 leading-relaxed max-w-4xl mx-auto">
              <p className="text-lg">
                <strong>{t('homepage.importance.text1Part1')}</strong>{t('homepage.importance.text1Part2')}
              </p>
              
              <p>
                {t('homepage.importance.text2Part1')}
                <strong>{t('homepage.importance.text2Part2')}</strong>
              </p>
            </div>
          </Card>

          {/* Key Benefits */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <Card className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                  <span className="text-purple-600">{getIcon('heart', 24)}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-800">🤝 {t('homepage.benefits.brotherhood.title')}</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                {t('homepage.benefits.brotherhood.description')}
              </p>
            </Card>
            
            <Card className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                  <span className="text-green-600">{getIcon('users', 24)}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-800">🌍 {t('homepage.benefits.global.title')}</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                {t('homepage.benefits.global.description')}
              </p>
            </Card>
          </div>
          
          {/* How It Works Section */}
          <Card className="p-8 mb-8">
            <h3 className="text-2xl font-bold text-center text-gray-800 mb-8">🌟 {t('homepage.howItWorks.title')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🕰️</span>
                </div>
                <h4 className="font-bold text-lg mb-2 text-gray-800">{t('homepage.howItWorks.realSupport.title')}</h4>
                <p className="text-sm text-gray-600">
                  {t('homepage.howItWorks.realSupport.description')}
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🤝</span>
                </div>
                <h4 className="font-bold text-lg mb-2 text-gray-800">{t('homepage.howItWorks.brotherhood.title')}</h4>
                <p className="text-sm text-gray-600">
                  {t('homepage.howItWorks.brotherhood.description')}
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">📊</span>
                </div>
                <h4 className="font-bold text-lg mb-2 text-gray-800">{t('homepage.howItWorks.accountability.title')}</h4>
                <p className="text-sm text-gray-600">
                  {t('homepage.howItWorks.accountability.description')}
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🌍</span>
                </div>
                <h4 className="font-bold text-lg mb-2 text-gray-800">{t('homepage.howItWorks.global.title')}</h4>
                <p className="text-sm text-gray-600">
                  {t('homepage.howItWorks.global.description')}
                </p>
              </div>
            </div>
          </Card>

          {/* Call to Action */}
          <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white p-8 rounded-xl text-center">
            <h3 className="text-2xl font-bold mb-4">✨ {t('homepage.results.title')}</h3>
            <p className="text-lg leading-relaxed mb-6">
              {t('homepage.results.description')}
            </p>
            <div className="inline-flex items-center space-x-2 bg-white bg-opacity-20 px-6 py-3 rounded-full">
              <span className="text-xl">🤲</span>
              <span className="font-medium">{t('homepage.results.cta')}</span>
            </div>
          </div>
        </div>
      </Section>

      <Section title={t('faq.title')} subtitle={t('faq.subtitle')} className="bg-muted/20" data-section="qa">
        <div className="max-w-4xl mx-auto">
          <Accordion items={FAQ_DATA} />
        </div>
      </Section>

      {/* Social Media Section */}
      <Section className="bg-white">
        <div className="max-w-4xl mx-auto">
          <SocialMediaCard />
        </div>
      </Section>

      <Section
        title={t('homepage.cta.title')}
        subtitle={t('homepage.cta.subtitle')}
        gradient
      >
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {user ? (
            <Button 
              className="bg-white text-primary hover:bg-gray-100"
              onClick={() => navigate('/profile')}
            >
              {t('nav.profile')}
            </Button>
          ) : (
            <>
              <Button 
                className="bg-white text-primary hover:bg-gray-100"
                onClick={() => navigate('/login')}
              >
                {t('auth.register')}
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/login')}
              >
                {t('auth.login')}
              </Button>
            </>
          )}
        </div>
      </Section>

      {/* Sponsors Section */}
      <Sponsors />

      <Footer />
    </div>
  )
}

export default Homepage